import {
  Box, Chip, Stack, TableCell, TableRow, Typography,
} from '@mui/material';
import moment from 'moment';
import Moment from 'react-moment';
import { useTranslation } from 'react-i18next';
import { CustomTooltip } from '../../../components/CustomTooltip';
import { DATE_FORMAT } from '../../../consts';
import {
  ReferralEligibility,
  ReferralSource,
} from '../../../enums/referral.enum';
import STRING_KEYS from '../../../language/keys';
import { Referral, ReferralPosition, ReferralReward } from '../../../@types/Referral';
import { ReferralRowActions } from '../ReferralRowActions';
import { PendingReferralAction } from '../referrals.types';
import { isFailedPayoutReward } from '../referrals.utils';
import style from './style';

type ReferralsRowItemProps = {
  referral: Referral,
  onAction: (action: PendingReferralAction) => void,
};

export const ReferralsRowItem = ({ referral, onAction }: ReferralsRowItemProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const renderDate = (date: string) => <Moment format={ DATE_FORMAT.DATE }>{ date }</Moment>;

  const renderReward = (reward: ReferralReward) => {
    const option = reward.option ? translate(STRING_KEYS.REFERRAL_PATHS[reward.option]) : '';
    const amount = reward.amountUsd ? ` $${reward.amountUsd}` : '';
    const isFailed = isFailedPayoutReward(reward);

    return (
      <Box key={ reward.id }>
        <Typography variant="body2" color={ isFailed ? 'error' : undefined }>
          { translate(STRING_KEYS.REFERRALS.MILESTONE, { milestone: reward.milestone }) }
          { ` · ${option}${amount} · ` }
          { translate(STRING_KEYS.REFERRAL_REWARD_STATUSES[reward.status]) }
        </Typography>
        { reward.fallbackReason && (
          <Typography variant="caption" color="textSecondary">
            { translate(STRING_KEYS.REFERRALS.FALLBACK, {
              reason: translate(STRING_KEYS.REFERRAL_FALLBACK_REASONS[reward.fallbackReason]),
            }) }
          </Typography>
        ) }
        { reward.deliveryEmail && (
          <Typography variant="caption" color="textSecondary" display="block">
            { reward.deliveryEmail }
          </Typography>
        ) }
      </Box>
    );
  };

  const renderPosition = (position: ReferralPosition) => {
    const renderEnd = () => {
      if (position.endedAt) {
        return translate(STRING_KEYS.REFERRALS.POSITION_ENDED, {
          date: moment(position.endedAt).format(DATE_FORMAT.DATE),
          reason: position.endReason ? translate(STRING_KEYS.REFERRAL_POSITION_END_REASONS[position.endReason]) : '',
        });
      }
      if (position.endsAt) {
        return translate(STRING_KEYS.REFERRALS.POSITION_UNTIL, {
          date: moment(position.endsAt).format(DATE_FORMAT.DATE),
        });
      }

      return translate(STRING_KEYS.REFERRALS.POSITION_ONGOING);
    };

    return (
      <Box key={ position.id }>
        <Typography variant="body2">
          { renderDate(position.startsAt) }
        </Typography>
        <Typography variant="caption" color="textSecondary">
          { renderEnd() }
        </Typography>
      </Box>
    );
  };

  const isEligible = referral.eligibility === ReferralEligibility.Eligible;

  return (
    <TableRow hover>
      <TableCell sx={ style.wideCell.sx }>
        <CustomTooltip title={ referral.referrerEmail || '' }>
          <Box>
            <Typography noWrap variant="subtitle2">
              { referral.referrerName || translate(STRING_KEYS.REFERRALS.UNKNOWN_REFERRER) }
            </Typography>
            <Typography noWrap variant="body2" color="textSecondary">
              { referral.referrerEmail }
            </Typography>
          </Box>
        </CustomTooltip>
        { referral.source === ReferralSource.EmailIntro && (
          <Chip size="small" label={ translate(STRING_KEYS.REFERRALS.EMAIL_INTRO) } />
        ) }
      </TableCell>

      <TableCell sx={ style.wideCell.sx }>
        <Typography noWrap variant="subtitle2">
          { referral.referredCompanyName || translate(STRING_KEYS.REFERRALS.UNKNOWN_COMPANY) }
        </Typography>
        <Typography noWrap variant="body2" color="textSecondary">
          { referral.referredEmail || referral.referredDomain }
        </Typography>
      </TableCell>

      <TableCell sx={ style.narrowCell.sx }>
        <Typography variant="caption">
          <Moment format={ DATE_FORMAT.TABLE_LIST }>{ referral.createdAt }</Moment>
        </Typography>
      </TableCell>

      <TableCell sx={ style.narrowCell.sx }>
        <Typography variant="body2">
          { referral.path ?
            translate(STRING_KEYS.REFERRAL_PATHS[referral.path]) :
            translate(STRING_KEYS.REFERRALS.NO_PATH) }
        </Typography>
      </TableCell>

      <TableCell sx={ style.narrowCell.sx }>
        <Typography variant="body2">
          { translate(STRING_KEYS.REFERRAL_STATUSES[referral.status]) }
        </Typography>
      </TableCell>

      <TableCell sx={ style.narrowCell.sx }>
        <Typography variant="body2" color={ isEligible ? undefined : 'error' }>
          { translate(STRING_KEYS.REFERRAL_ELIGIBILITIES[referral.eligibility]) }
        </Typography>
        { referral.ineligibilityReason && (
          <Typography variant="caption" color="textSecondary" display="block">
            { translate(STRING_KEYS.REFERRAL_INELIGIBILITY_REASONS[referral.ineligibilityReason]) }
          </Typography>
        ) }
        { referral.eligibilityOverriddenAt && (
          <Typography variant="caption" color="textSecondary" display="block">
            { translate(STRING_KEYS.REFERRALS.OVERRIDDEN) }
            { ' ' }
            { renderDate(referral.eligibilityOverriddenAt) }
          </Typography>
        ) }
      </TableCell>

      <TableCell sx={ style.narrowCell.sx }>
        { referral.referredAccountId ? (
          <CustomTooltip title={ referral.referredAccountId }>
            <Typography noWrap variant="body2" sx={ style.ellipsis.sx }>
              { referral.referredAccountId }
            </Typography>
          </CustomTooltip>
        ) : (
          <Typography variant="body2" color="textSecondary">
            { translate(STRING_KEYS.REFERRALS.NOT_LINKED) }
          </Typography>
        ) }
        { referral.payingOutsideStripe && (
          <Chip
            size="small"
            label={ translate(STRING_KEYS.REFERRALS.PAYING_OUTSIDE_STRIPE) }
            sx={ style.wrappingChip.sx }
          />
        ) }
      </TableCell>

      <TableCell sx={ style.wideCell.sx }>
        <Stack spacing={ 1 }>
          { (referral.rewards || []).map(renderReward) }
        </Stack>
      </TableCell>

      <TableCell sx={ style.narrowCell.sx }>
        <Stack spacing={ 1 }>
          { (referral.positions || []).map(renderPosition) }
        </Stack>
      </TableCell>

      <TableCell sx={ style.menuCell.sx }>
        <ReferralRowActions referral={ referral } onAction={ onAction } />
      </TableCell>
    </TableRow>
  );
};
